import { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'motion/react';

export interface AnimatedCountProps {
  /** The numeric value to display. */
  value: number;
  /** Optional class applied to the digit span (e.g. text color/size). */
  className?: string;
  /**
   * Stable identity for this counter (e.g. a file/folder id). When provided,
   * the last-seen value is remembered across unmount/remount so the pop still
   * fires when the user navigates away and back to a now-changed count. Omit
   * for purely in-place counters where a remount should reset to static.
   */
  animationKey?: string;
}

// Count-flip + scale-pop tokens from. Kept short so rapid updates
// (live comment counts) stay snappy rather than queuing a long sequence.
const FLIP_DURATION_S = 0.22;
const POP_SCALE = 1.18;
const POP_OFFSET_PX = 6;

// Remembers the last value rendered for a given `animationKey` so the pop can
// fire across remounts (navigation). Session-scoped and bounded by the number
// of distinct counters viewed — values are small ints, so growth is negligible.
const lastSeenByKey = new Map<string, number>();

/**
 * Renders a number that flips and pops when its value changes. The first time a
 * given value is seen (initial paint, or a brand-new counter) it renders
 * static — only genuine changes animate — so list loads don't trigger a wave of
 * motion. Respects `prefers-reduced-motion`.
 */
export function AnimatedCount(props: AnimatedCountProps) {
  const { value, className, animationKey } = props;
  const shouldReduceMotion = useReducedMotion();

  // Seed the "previous" value from the cross-mount cache when an animationKey
  // is supplied, otherwise from the current value (so a fresh, keyless counter
  // starts static). Compared during render — not via an effect — so the very
  // first change animates rather than being a render late.
  const prevValueRef = useRef<number>(
    animationKey !== undefined && lastSeenByKey.has(animationKey)
      ? (lastSeenByKey.get(animationKey) as number)
      : value,
  );
  const changed = prevValueRef.current !== value;

  useEffect(() => {
    prevValueRef.current = value;
    if (animationKey !== undefined) lastSeenByKey.set(animationKey, value);
  }, [value, animationKey]);

  if (shouldReduceMotion) {
    return <span className={className}>{value}</span>;
  }

  return (
    // Keyed on the value so each change remounts the span, replaying `initial`
    // → `animate` (a re-render alone would not, since `animate` is constant).
    <motion.span
      key={value}
      className={`inline-block ${className ?? ''}`}
      initial={changed ? { y: -POP_OFFSET_PX, opacity: 0, scale: POP_SCALE } : false}
      animate={{ y: 0, opacity: 1, scale: 1 }}
      transition={{ duration: FLIP_DURATION_S, ease: 'easeOut' }}
    >
      {value}
    </motion.span>
  );
}

AnimatedCount.displayName = 'AnimatedCount';
